/**
 * Turns a plan into the canvas view model.
 *
 * The plan carries no coordinates, so positions are derived here and are never
 * persisted: everyone who opens a procedure sees the same picture, and a drag
 * is a local nudge rather than a change to the document. Layout is a pure
 * function of step order and exits, so it is stable across reloads.
 */

import {
	AOPEdge,
	AOPDraftPlan,
	AOPSuggestion,
	AOPViolation,
	AOPPoint,
	AOPStepName,
	AOP_AFTER_EXIT,
	AOP_OUT_HANDLE,
} from '../../../types/AOPTypes';
import {
	AOP_NODE_FALLBACK_HEIGHT,
	AOP_NODE_FALLBACK_WIDTH,
	exitsOf,
	stepHasExits,
	stepNumberOf,
	successorsOf,
} from './AOPPlanUtils';

/** Space between columns and between stacked cards — the only fixed numbers. */
const COLUMN_GAP = 110;
const ROW_GAP = 60;

/**
 * The channels under the graph that long edges run along, and how far apart
 * they sit. See `AOPRoutedEdge` for why they exist.
 */
const CHANNEL_GAP = 28;
const FIRST_CHANNEL = 48;

/** The edge type a routed edge asks for; registered on the canvas. */
export const AOP_ROUTED_EDGE = 'aopRouted';

/**
 * What each card measures, by step id, as React Flow reports it. Empty on the
 * first render, filled from then on; the fallbacks above stand in until a card
 * has been measured, so nothing here decides how big a card is.
 */
export interface AOPNodeSizes {
	[nodeId: string]: { width: number; height: number };
}

const widthOf = (sizes: AOPNodeSizes, nodeId: string): number =>
	sizes[nodeId] && sizes[nodeId].width ? sizes[nodeId].width : AOP_NODE_FALLBACK_WIDTH;

const heightOf = (sizes: AOPNodeSizes, nodeId: string): number =>
	sizes[nodeId] && sizes[nodeId].height ? sizes[nodeId].height : AOP_NODE_FALLBACK_HEIGHT;

/* -------------------------------------------------------------------------- */
/*                                   Layout                                   */
/* -------------------------------------------------------------------------- */

/**
 * Column per step, by shortest distance from the entry step. Steps nothing
 * reaches — which the service reports as a finding — are parked in a trailing
 * column rather than piled on the entry.
 */
const columnsOf = (plan: AOPDraftPlan): { [stepId: string]: number } => {
	const edges = successorsOf(plan);
	const columns: { [stepId: string]: number } = {};
	const entry = plan.steps[0];

	if (!entry) {
		return columns;
	}

	let frontier: AOPStepName[] = [entry.name];
	let depth = 0;

	while (frontier.length > 0) {
		const next: AOPStepName[] = [];

		frontier.forEach((stepId) => {
			if (columns[stepId] !== undefined) {
				return;
			}
			columns[stepId] = depth;
			(edges[stepId] ? edges[stepId] : []).forEach((target) => next.push(target));
		});

		frontier = next;
		depth += 1;
	}

	const orphanColumn = depth;
	plan.steps.forEach((step) => {
		if (columns[step.name] === undefined) {
			columns[step.name] = orphanColumn;
		}
	});

	return columns;
};

/**
 * Where each card sits. Columns are as wide as their widest card and rows as
 * tall as the card above, both measured — a long question that wraps to three
 * lines pushes what is below it down instead of overlapping it, and a column of
 * narrow cards does not leave a gap the width of the widest card on the canvas.
 */
export const layoutPositions = (
	plan: AOPDraftPlan,
	sizes: AOPNodeSizes
): { [stepId: string]: AOPPoint } => {
	const columns = columnsOf(plan);
	const positions: { [stepId: string]: AOPPoint } = {};

	const columnWidths: { [column: number]: number } = {};
	let lastColumn = 0;

	plan.steps.forEach((step) => {
		const column = columns[step.name];
		const width = widthOf(sizes, step.name);
		columnWidths[column] = Math.max(columnWidths[column] ? columnWidths[column] : 0, width);
		lastColumn = Math.max(lastColumn, column);
	});

	const columnX: { [column: number]: number } = {};
	let x = 0;

	for (let column = 0; column <= lastColumn; column += 1) {
		columnX[column] = x;
		x += (columnWidths[column] ? columnWidths[column] : AOP_NODE_FALLBACK_WIDTH) + COLUMN_GAP;
	}

	// Walk in plan order so two steps in the same column always stack in the
	// order the author reads them in the step list.
	const nextY: { [column: number]: number } = {};

	plan.steps.forEach((step) => {
		const column = columns[step.name];
		const y = nextY[column] === undefined ? 0 : nextY[column];
		nextY[column] = y + heightOf(sizes, step.name) + ROW_GAP;
		positions[step.name] = { x: columnX[column], y };
	});

	return positions;
};

/* -------------------------------------------------------------------------- */
/*                                   Edges                                    */
/* -------------------------------------------------------------------------- */

/**
 * Canvas edges. An exit becomes an edge from the handle named after it; a
 * `communicate` step has no exits and simply falls through to the next step in
 * the list, which is drawn from its single unnamed handle.
 */
export const planEdges = (plan: AOPDraftPlan): AOPEdge[] => {
	const stepIds = new Set(plan.steps.map((step) => step.name));
	const edges: AOPEdge[] = [];

	// Position in the plan, to tell an edge that moves on from one that doubles
	// back. "Backwards" is positional rather than a true back-edge test: it is
	// what somebody reading the chart means by going back a step, and a retry
	// loop always points at a step they have already read.
	const positions: { [stepId: string]: number } = {};
	plan.steps.forEach((step, index) => {
		positions[step.name] = index;
	});

	plan.steps.forEach((step, index) => {
		exitsOf(step).forEach((exit) => {
			// A target that resolves to nothing is reported by the service as a
			// finding; there is no edge to draw for it.
			if (!stepIds.has(exit.target)) {
				return;
			}

			edges.push({
				id: `${step.name}:${exit.name}->${exit.target}`,
				source: step.name,
				sourceHandle: exit.name,
				target: exit.target,
				targetHandle: 'in',
				/*
				  A communicate step's `after` is drawn grey: it is the one exit
				  nobody chose, taken as soon as the note is written. Any exit
				  pointing at a step already read is a loop, and drawn dashed —
				  which `after` can now be, and could not before.
				*/
				className:
					positions[exit.target] <= index
						? 'aop-edge-backwards'
						: exit.name === AOP_AFTER_EXIT
						? 'aop-edge-implicit'
						: undefined,
			});
		});
	});

	return edges;
};

/* -------------------------------------------------------------------------- */
/*                               View model                                   */
/* -------------------------------------------------------------------------- */

/**
 * The Start block is drawn but is not a step: entry is `steps[0]` and the
 * trigger criteria live on the Scenario. It is given a column of its own to the
 * left so every real step keeps the position it would have had without it, and
 * it carries no step number — the Steps panel and the cards both count the
 * plan, which it is not part of.
 */
export const AOP_START_NODE_ID = '__start__';

export interface AOPCanvasModel {
	nodes: {
		id: string;
		type: 'aopStep' | 'aopStart';
		position: AOPPoint;
		data: any;
	}[];
	edges: AOPEdge[];
}

/**
 * Sends the edges that cannot go straight under the graph instead.
 *
 * An edge between neighbouring columns has an empty gutter to turn in, so
 * React Flow's own smoothstep draws it cleanly and it is left alone. One that
 * skips a column, or doubles back to a column already passed, would put its
 * vertical segment inside a column and run beneath the cards stacked there —
 * which is the chart being hard to follow. Those get a channel below every
 * card, one each.
 *
 * Channels are handed out shortest span first, so a long edge runs deeper than
 * the short ones and they nest rather than cross. Span is measured in columns
 * because that is what decides whether an edge has anything in its way.
 */
const routeEdges = (
	edges: AOPEdge[],
	plan: AOPDraftPlan,
	nodes: { id: string; position: AOPPoint }[],
	sizes: AOPNodeSizes
): AOPEdge[] => {
	const columns = columnsOf(plan);
	// The Start block sits a column to the left of the entry step, and nothing
	// points back at it, so it only ever has the shortest kind of edge.
	columns[AOP_START_NODE_ID] = -1;

	const bottom = nodes.reduce(
		(lowest, node) => Math.max(lowest, node.position.y + heightOf(sizes, node.id)),
		0
	);

	const spanOf = (edge: AOPEdge): number => {
		const from = columns[edge.source];
		const to = columns[edge.target];
		return from === undefined || to === undefined ? 0 : to - from;
	};

	// Forward to the very next column is the one case with a clear gutter.
	const needsChannel = (edge: AOPEdge): boolean => spanOf(edge) !== 1;

	const routed = edges
		.filter(needsChannel)
		.slice()
		.sort((left, right) => {
			const difference = Math.abs(spanOf(left)) - Math.abs(spanOf(right));
			// Ties broken by id so the picture is the same on every render.
			return difference !== 0 ? difference : left.id.localeCompare(right.id);
		});

	const channels: { [edgeId: string]: number } = {};
	routed.forEach((edge, lane) => {
		channels[edge.id] = bottom + FIRST_CHANNEL + lane * CHANNEL_GAP;
	});

	return edges.map((edge) =>
		channels[edge.id] === undefined
			? edge
			: { ...edge, type: AOP_ROUTED_EDGE, data: { channelY: channels[edge.id] } }
	);
};

export const buildCanvasModel = (
	plan: AOPDraftPlan,
	suggestions: AOPSuggestion[],
	violations: AOPViolation[],
	scenario: any,
	sizes: AOPNodeSizes
): AOPCanvasModel => {
	const positions = layoutPositions(plan, sizes);
	const entry = plan.steps[0];

	const stepNodes = plan.steps.map((step, index) => ({
		id: step.name,
		type: 'aopStep' as 'aopStep',
		position: positions[step.name],
		data: {
			step,
			stepNumber: stepNumberOf(index),
			suggestions: suggestions.filter(
				(suggestion) => suggestion.anchor === step.name && suggestion.status !== 'removed'
			),
			violations: violations.filter((violation) => violation.anchor === step.name),
		},
	}));

	const startNodes = entry
		? [
				{
					id: AOP_START_NODE_ID,
					type: 'aopStart' as 'aopStart',
					position: { x: -(widthOf(sizes, AOP_START_NODE_ID) + COLUMN_GAP), y: 0 },
					data: { scenario },
				},
		  ]
		: [];

	const startEdges: AOPEdge[] = entry
		? [
				{
					id: `${AOP_START_NODE_ID}->${entry.name}`,
					source: AOP_START_NODE_ID,
					sourceHandle: AOP_OUT_HANDLE,
					target: entry.name,
					targetHandle: 'in',
					className: 'aop-edge-implicit',
				},
		  ]
		: [];

	const nodes = (startNodes as any[]).concat(stepNodes as any[]);

	return {
		nodes,
		edges: routeEdges(startEdges.concat(planEdges(plan)), plan, nodes, sizes),
	};
};
